const ALLOWED_CHAT_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'audio/webm',
  'audio/ogg',
  'audio/mp4',
]);

const ALLOWED_VISUALIZATION_VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-msvideo',
]);

const ALLOWED_VISUALIZATION_PHOTO_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

const ALLOWED_RECOMMENDATION_LETTER_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

const ALLOWED_TOOTH_SPACING_MAP_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

const ALLOWED_PLAN_SCHEME_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB
const MAX_RECOMMENDATION_LETTER_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_TOOTH_SPACING_MAP_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_PLAN_SCHEME_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_VISUALIZATION_VIDEO_BYTES = 20 * 1024 * 1024; // 20 MB
const MAX_VISUALIZATION_PHOTO_BYTES = 10 * 1024 * 1024; // 10 MB

// 3Shape exports carry inconsistent MIME types across browsers and OSes, so the
// `.stl` extension is treated as authoritative when the type is missing or generic.
const ALLOWED_MESH_MIME_TYPES = new Set([
  'model/stl',
  'application/sla',
  'application/vnd.ms-pki.stl',
  'application/x-navistyle',
]);

const MAX_MESH_BYTES = 100 * 1024 * 1024; // 100 MB — mirrors WAF_TREATMENT_MESH_MAX_BYTES

const VIDEO_EXTENSIONS = new Set(['.mp4', '.mov', '.webm', '.avi']);

export function hasVideoExtension(name: string): boolean {
  const dot = name.lastIndexOf('.');
  if (dot < 0) return false;
  return VIDEO_EXTENSIONS.has(name.slice(dot).toLowerCase());
}

export type FileValidationError = 'type' | 'size';

export function validateChatAttachment(file: File): FileValidationError | null {
  if (!ALLOWED_CHAT_MIME_TYPES.has(file.type)) return 'type';
  if (file.size > MAX_FILE_BYTES) return 'size';
  return null;
}

export function validateVoiceMessage(file: Blob): FileValidationError | null {
  if (file.size > MAX_FILE_BYTES) return 'size';
  return null;
}

export function validateVisualizationVideo(file: File): FileValidationError | null {
  const mime = file.type;
  const mimeOk = ALLOWED_VISUALIZATION_VIDEO_MIME_TYPES.has(mime);
  const isAmbiguousMime = !mime || mime === 'application/octet-stream';
  if (!mimeOk && !(isAmbiguousMime && hasVideoExtension(file.name))) return 'type';
  if (file.size > MAX_VISUALIZATION_VIDEO_BYTES) return 'size';
  return null;
}

export function validateVisualizationPhoto(file: File): FileValidationError | null {
  if (!ALLOWED_VISUALIZATION_PHOTO_MIME_TYPES.has(file.type)) return 'type';
  if (file.size > MAX_VISUALIZATION_PHOTO_BYTES) return 'size';
  return null;
}

export function isVisualizationVideoMime(mime: string): boolean {
  return ALLOWED_VISUALIZATION_VIDEO_MIME_TYPES.has(mime);
}

export function isVisualizationPhotoMime(mime: string): boolean {
  return ALLOWED_VISUALIZATION_PHOTO_MIME_TYPES.has(mime);
}

export function validateRecommendationLetterImage(file: File): FileValidationError | null {
  if (!ALLOWED_RECOMMENDATION_LETTER_MIME_TYPES.has(file.type)) return 'type';
  if (file.size > MAX_RECOMMENDATION_LETTER_BYTES) return 'size';
  return null;
}

function hasStlExtension(name: string): boolean {
  return name.trim().toLowerCase().endsWith('.stl');
}

/** Client-side gate for treatment mesh uploads; the server re-validates and sniffs the header. */
export function validateTreatmentMesh(file: File): FileValidationError | null {
  if (!ALLOWED_MESH_MIME_TYPES.has(file.type) && !hasStlExtension(file.name)) return 'type';
  if (file.size > MAX_MESH_BYTES) return 'size';
  return null;
}

export function validateToothSpacingMapImage(file: File): FileValidationError | null {
  if (!ALLOWED_TOOTH_SPACING_MAP_MIME_TYPES.has(file.type)) return 'type';
  if (file.size > MAX_TOOTH_SPACING_MAP_BYTES) return 'size';
  return null;
}

/** Mirrors server gates for optional treatment-plan scheme images. */
export function validatePlanSchemeImage(file: File): FileValidationError | null {
  if (!ALLOWED_PLAN_SCHEME_MIME_TYPES.has(file.type)) return 'type';
  if (file.size > MAX_PLAN_SCHEME_BYTES) return 'size';
  return null;
}

export const HELP_ARTICLE_PDF_MAX_BYTES = 60 * 1024 * 1024; // 60 MB — mirrors WAF_HELP_ARTICLE_PDF_MAX_BYTES

/**
 * Size gate for help-article hero PDFs, checked before a large upload is sent.
 * Type filtering is left to the input's `accept` list and the server's MIME check.
 */
export function validateHelpArticlePdf(file: File): FileValidationError | null {
  if (file.size > HELP_ARTICLE_PDF_MAX_BYTES) return 'size';
  return null;
}
